import { useParams, Link } from "react-router-dom";
import { categories, siteInfo } from "@/mocks/site";
import { articles } from "@/mocks/articles";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/base/ScrollToTop";
import SEOMeta from "@/components/base/SEOMeta";
import Breadcrumb from "@/components/base/Breadcrumb";
import ArticleCard from "@/components/base/ArticleCard";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.category === slug);

  if (!category) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground-900 mb-3">分类未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const colorMap: Record<string, { iconBg: string; text: string; tag: string }> = {
    primary: { iconBg: "bg-primary-100", text: "text-primary-600", tag: "bg-primary-50 text-primary-600" },
    accent: { iconBg: "bg-accent-100", text: "text-accent-600", tag: "bg-accent-50 text-accent-600" },
    secondary: { iconBg: "bg-secondary-100", text: "text-secondary-600", tag: "bg-secondary-50 text-secondary-600" },
  };
  const colors = colorMap[category.color] || colorMap.primary;

  return (
    <>
      <SEOMeta
        title={`${category.name} - ${siteInfo.name} | 蘑菇tv成品人资源分类`}
        description={`蘑菇tv成品人${category.name}频道，${category.description}。浏览${categoryArticles.length}篇精选内容，助你快速找到所需优质资源。`}
        keywords={`蘑菇tv成品人,${category.name},蘑菇tv成品人资源,蘑菇tv成品人频道`}
        canonicalUrl={`https://mogutv.com/category/${slug}`}
        ogTitle={`${category.name} - ${siteInfo.name}`}
        ogDescription={`蘑菇tv成品人${category.name}频道，${category.description}`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: `${category.name} - ${siteInfo.name}`,
          description: category.description,
          url: `https://mogutv.com/category/${slug}`,
          inLanguage: "zh-CN",
          isPartOf: {
            "@type": "WebSite",
            name: siteInfo.name,
            url: "https://mogutv.com",
          },
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-14">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: "首页", href: "/" },
                { label: category.name },
              ]}
            />

            {/* Category header */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-12 h-12 flex items-center justify-center rounded-xl ${colors.iconBg}`}>
                  <i className={`${category.icon} text-2xl ${colors.text}`}></i>
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
                    {category.name}
                  </h1>
                  <p className="text-sm text-foreground-600 mt-1">{category.description}</p>
                </div>
              </div>
            </div>

            {/* Category tabs */}
            <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                    cat.slug === slug
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200"
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {/* Articles */}
            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {categoryArticles.map((article) => (
                  <ArticleCard key={article.slug} article={article} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <h3 className="text-lg font-medium text-foreground-700 mb-2">暂无内容</h3>
                <p className="text-sm text-foreground-500">该分类下暂时没有内容，请稍后再来</p>
              </div>
            )}
          </div>
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}